import { orgAPI } from "@/api/api";
import { useUpdateCanvas } from "@/api/canvas";
import { useSetCanvasFolder } from "@/api/folders";
import type {
  ObjectActionDestinationSubmenu,
  ResolvedObjectAction,
  TransferDestination,
} from "@/object-actions/types";
import type { Canvas } from "@cohesive/api-client";
import { useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { useWritableWorkspaceIds } from "./useWritableWorkspaces";

/**
 * Move to for canvases. Canvases live in a workspace's canvas folders, not in
 * Library path folders, so the picker drills the `folders` tree and the commit
 * files the canvas into whichever folder was chosen.
 *
 * Copy is deliberately absent. Duplicating a canvas would have to decide what
 * happens to the references it holds, and that is unspecified; Move needs no
 * such ruling because the canvas keeps its identity and its contents.
 */
export function useCanvasMoveCommit(
  canvas: Canvas | null,
): (destination: TransferDestination) => void {
  const updateCanvas = useUpdateCanvas({});
  const setCanvasFolder = useSetCanvasFolder();

  return useCallback(
    (destination: TransferDestination) => {
      if (!canvas?.id) return;
      void (async () => {
        try {
          if (destination.workspaceId !== canvas.workspace_id) {
            // Re-read immediately before the PUT so the write carries the
            // freshest server state rather than a stale cached copy.
            const latest = await orgAPI.canvas.getById(canvas.id);
            await updateCanvas.mutateAsync({
              id: canvas.id,
              data: {
                name: latest.name,
                description: latest.description,
                metadata: latest.metadata,
                workspace_id: destination.workspaceId,
              },
            });
          }
          // Files it into the chosen canvas folder, or the workspace root —
          // which also clears a folder membership left behind by a
          // cross-workspace move.
          await setCanvasFolder.mutateAsync({
            canvasId: canvas.id,
            folderId: destination.folderId ?? null,
            workspaceId: destination.workspaceId,
          });
          toast.success(`Moved to ${destination.label}`);
        } catch {
          toast.error("Failed to move canvas");
        }
      })();
    },
    [canvas, setCanvasFolder, updateCanvas],
  );
}

/**
 * Decorate a canvas's resolved actions with the workspace destination
 * submenu. Returns the actions unchanged when `move` isn't on the menu.
 */
export function useAttachCanvasTransferSubmenu(
  actions: ResolvedObjectAction[],
  canvas: Canvas | null,
): ResolvedObjectAction[] {
  const commitMove = useCanvasMoveCommit(canvas);
  const hasMove = actions.some((a) => a.id === "move");
  const writableWorkspaceIds = useWritableWorkspaceIds(hasMove);

  return useMemo(() => {
    if (!canvas?.id || !hasMove) return actions;
    const submenu: ObjectActionDestinationSubmenu = {
      kind: "workspace-destination",
      operation: "move",
      commit: commitMove,
      treeSource: "canvas-folders",
      currentWorkspaceId: canvas.workspace_id ?? undefined,
      writableWorkspaceIds,
    };
    return actions.map((action) =>
      action.id === "move" ? { ...action, submenu } : action,
    );
  }, [actions, canvas, commitMove, hasMove, writableWorkspaceIds]);
}
